import { useState, useCallback } from 'react'
import { apiFetch, ApiError } from '../../../platform/lib/apiClient'

// Spec 077 — déclenchement d'un audit depuis le portail (action « Lancer l'audit »).

export interface StartAuditResult {
  executionId: string
  executionName: string
  status: string
  startedAt: string
}

export interface StartAuditError {
  accountId: string
  code: string
  message: string
  missing?: string[]
}

function friendlyMessage(err: ApiError): string {
  switch (err.code) {
    case 'AUDIT_ALREADY_RUNNING':
      return 'Un audit est déjà en cours pour ce client.'
    case 'TENANT_AUDIT_PARAMS_INCOMPLETE':
      return 'Paramètres d\'audit du client incomplets — complétez la fiche client avant de lancer.'
    case 'JOB_START_FAILED':
      return 'Le démarrage de l\'audit a échoué. Veuillez réessayer.'
    case 'EVAL_NOT_ELIGIBLE':
      return 'L\'audit d\'évaluation n\'est possible que pour un client en offre « gratuit » ou « prospect ».'
    case 'FORBIDDEN':
    case 'AUDIT_TRIGGER_FORBIDDEN':
      return 'Vous n\'êtes pas autorisé à lancer un audit pour ce client.'
    default:
      return err.message ?? 'Erreur lors du lancement de l\'audit.'
  }
}

// Spec 088/089 — `phase='preflight'` déclenche un preflight (validation pré-requis only) ;
// défaut/absence = audit complet (full). Le backend valide le contrat (CONTRACT-execution v1.6).
export type AuditPhase = 'full' | 'preflight'

// Spec 125 — `generateEvalFile` demande la génération du fichier d'évaluation (offre gratuit/prospect).
export interface StartAuditOptions {
  generateEvalFile?: boolean
}

interface UseStartAuditState {
  startAudit: (accountId: string, phase?: AuditPhase, opts?: StartAuditOptions) => Promise<StartAuditResult | null>
  starting: string | null
  error: StartAuditError | null
  clearError: () => void
}

export function useStartAudit(): UseStartAuditState {
  const [starting, setStarting] = useState<string | null>(null)
  const [error, setError] = useState<StartAuditError | null>(null)

  const startAudit = useCallback(async (accountId: string, phase?: AuditPhase, opts?: StartAuditOptions): Promise<StartAuditResult | null> => {
    setStarting(accountId)
    setError(null)
    try {
      const body: Record<string, unknown> = { accountId }
      if (phase === 'preflight') body.phase = phase
      if (opts?.generateEvalFile) body.generateEvalFile = true
      return await apiFetch<StartAuditResult>('/api/audit/start', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    } catch (err) {
      if (err instanceof ApiError) {
        const rawMissing = (err.extra as { missing?: unknown } | undefined)?.missing
        const missing = Array.isArray(rawMissing) ? (rawMissing as string[]) : undefined
        setError({ accountId, code: err.code, message: friendlyMessage(err), missing })
      } else {
        setError({ accountId, code: 'UNKNOWN', message: 'Erreur inattendue lors du lancement.' })
      }
      return null
    } finally {
      setStarting(null)
    }
  }, [])

  const clearError = useCallback(() => setError(null), [])

  return { startAudit, starting, error, clearError }
}
